import { useState, useEffect, useCallback } from 'react';

import { Button, DefaultSheet, SheetBody, Select, Col, FormGroup, Input, Label, Row } from 'twyne-new.ui';

import ColorPicker from '@/components/form/ColorPicker';
import ImageField from '@Components/custom_fields/fields/ImageField';

const IMAGE_POSITION_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
]

const PROPERTIES_STYLES = ['backgroundColor', 'fontColor', 'borderColor', 'borderSize', 'borderStyle', 'padding', 'fontSize']

const PROPERTIES_MAP = {
  backgroundColor: 'background-color',
  fontColor: 'color',
  borderColor: 'border-color',
  borderSize: 'border-width',
  borderStyle: 'border-style',
  padding: 'padding',
  fontSize: 'font-size',
}

export default function OptionProperties({ open, onClose, selectedAnswer, onChange, disabled, answers }) {
  const [dataAnswers, setDataAnswers] = useState(answers)
  const {
    backgroundColor,
    fontColor,
    borderColor,
    borderSize,
    borderStyle,
    padding,
    fontSize,
    class: className,
    imagePosition,
    imageHeight,
    imageWidth,
    src,
    alt,
  } = dataAnswers[selectedAnswer] || {}

  useEffect(() => {
    onChange('answers', dataAnswers)
  }, [dataAnswers, onChange])

  const handleChangeAwnser = useCallback((property, value) => {
    if (selectedAnswer === null) return;
    setDataAnswers(prevAnswers => {
      prevAnswers[selectedAnswer][property] = value;
      if (value === null) delete prevAnswers[selectedAnswer][property]

      return [...prevAnswers]
    })
  }, [selectedAnswer])

  const handleFileUpload = ({ value }) => {
    handleChangeAwnser('src', value.src)
    handleChangeAwnser('alt', value.name)
  }

  useEffect(() => {
    if (selectedAnswer === null) return;
    const stylesValues = {
      backgroundColor,
      fontColor,
      borderColor,
      borderSize,
      borderStyle,
      padding,
      fontSize,
    }
    const style = PROPERTIES_STYLES.reduce((acc, key) => {
      if (stylesValues[key]) {
        acc += `${PROPERTIES_MAP[key]}: ${key.includes("Color") ? `rgba(${stylesValues[key]})` : stylesValues[key]}!important;`
      }
      return acc
    }, '')

    setDataAnswers(prevAnswers => {
      prevAnswers[selectedAnswer].style = style;
      return [...prevAnswers]
    })
  }, [
    backgroundColor,
    fontColor,
    borderColor,
    borderSize,
    borderStyle,
    padding,
    fontSize,
    selectedAnswer,
  ])

  if (selectedAnswer === null) return null;

  return <DefaultSheet
    wrapperProps={{ open, onOpenChange: open => !open && onClose() }}
    title='Option properties'
  >
    <SheetBody>
      <Row>
        {!disabled && <>
          <Col size="4">
            <FormGroup>
              <Label>
                Background Color
                <Button className='ml-2' variant='outline' color='accent' size='sm' onClick={() => handleChangeAwnser('backgroundColor', null)}>
                  clear
                </Button>
              </Label>
              <ColorPicker
                value={backgroundColor || '255,255,255,1'}
                onChange={val => handleChangeAwnser('backgroundColor', val)} />
            </FormGroup>
          </Col>
          <Col size="4">
            <FormGroup>
              <Label>
                Font Color
                <Button className='ml-2' variant='outline' color='accent' size='sm' onClick={() => handleChangeAwnser('fontColor', null)}>
                  clear
                </Button>
              </Label>
              <ColorPicker
                value={fontColor || '255,255,255,1'}
                onChange={val => handleChangeAwnser('fontColor', val)} />
            </FormGroup>
          </Col>
          <Col size="3">
            <FormGroup>
              <Label>
                Border Color
                <Button className='ml-2' variant='outline' color='accent' size='sm' onClick={() => handleChangeAwnser('borderColor', null)}>
                  clear
                </Button>
              </Label>
              <ColorPicker
                value={borderColor || '255,255,255,1'}
                onChange={val => handleChangeAwnser('borderColor', val)} />
            </FormGroup>
          </Col>
        </>}
        <Col size="3">
          <FormGroup>
            <Label>border size</Label>
            <Input
              disabled={disabled}
              value={borderSize || ''}
              onChange={e => handleChangeAwnser('borderSize', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>border style</Label>
            <Input
              disabled={disabled}
              value={borderStyle || ''}
              onChange={e => handleChangeAwnser('borderStyle', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>padding</Label>
            <Input
              disabled={disabled}
              value={padding || ''}
              onChange={e => handleChangeAwnser('padding', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>font size</Label>
            <Input
              disabled={disabled}
              value={fontSize || ''}
              onChange={e => handleChangeAwnser('fontSize', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>class</Label>
            <Input
              disabled={disabled}
              value={className || ''}
              onChange={e => handleChangeAwnser('class', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>Image Position</Label>
            <Select
              disabled={disabled}
              clearable={false}
              name="imagePosition"
              value={imagePosition || ''}
              options={IMAGE_POSITION_OPTIONS}
              onChange={option => handleChangeAwnser('imagePosition', option.value)}
            />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>Image height</Label>
            <Input
              disabled={disabled}
              value={imageHeight || ''}
              onChange={e => handleChangeAwnser('imageHeight', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="3">
          <FormGroup>
            <Label>Image width</Label>
            <Input
              disabled={disabled}
              value={imageWidth || ''}
              onChange={e => handleChangeAwnser('imageWidth', e.target.value)} />
          </FormGroup>
        </Col>
        <Col size="12">
          <FormGroup>
            <Label>Button Image</Label>
            <ImageField
              value={{ src, name: alt }}
              onChange={handleFileUpload}
            />
          </FormGroup>
        </Col>
      </Row>
    </SheetBody>
  </DefaultSheet>
}
